import type { ParentType as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FormId } from '@risksmart-app/shared/forms/formConfigRegistry';
import { useMemo } from 'react';

import { useGetFormCustomisationByParentTypes } from '@/hooks/queries/form-configuration/useGetFormCustomisationByParentTypes';

import type { TableFields, TableRecord } from '../types';

export const useGetTableFormCustomisationData = <T extends TableRecord>(
  tableFields: TableFields<T>,
  customAttributeFormIds: Parent_Type_Enum[]
) => {
  // Form configuration required for custom labels
  const formIds = useMemo<Set<FormId>>(() => {
    const formIds = new Set<FormId>();
    Object.entries(tableFields).forEach(([_, fieldConfig]) => {
      if ('formId' in fieldConfig && fieldConfig.formId) {
        formIds.add(fieldConfig.formId);
      }
    });

    return formIds;
  }, [tableFields]);

  customAttributeFormIds.forEach((id) => formIds.add(id as unknown as FormId));

  // FormId is a subtype of ParentType (every form id is a parent-type value),
  // so this FormId[] assigns to the hook's ParentType[] directly.
  const parentTypes = useMemo(() => Array.from(formIds), [formIds]);

  // Flag-switched read: under the `trpc` flag this resolves via the data-layer
  // (frontend.formConfiguration.getFormCustomisation); otherwise it falls back
  // to the GraphQL document. Using the shared hook (rather than a raw Apollo
  // query) is what lets a custom field added via the tRPC createFormField
  // mutation refresh an already-mounted register table — the mutation
  // invalidates getFormCustomisation, so the column appears without a full
  // page remount. The previous raw Apollo query never saw the tRPC write and
  // left register custom-attribute columns stale under V3 (RSP-5866).
  const { data, loading } = useGetFormCustomisationByParentTypes({
    queryArgs: { parentTypes },
    shouldSkip: !formIds.size,
  });

  return { formConfiguration: data?.form_configuration ?? [], loading };
};
